import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { isEmpty, toUpper, includes, get, uniqueId } from "lodash";
import Button from "antd/lib/button";
import List from "antd/lib/list";
import Modal from "antd/lib/modal";
import Input from "antd/lib/input";
import Steps from "antd/lib/steps";
import { wrap as wrapDialog, DialogPropType } from "@/components/dialog-wrapper/DialogWrapper";
import Link from "@/components/Link";
import { PreviewCard } from "@/components/PreviewCard";
import EmptyState from "@/components/items-list/components/EmptyState";
import DynamicForm from "@/components/dynamic-form/DynamicForm";
import helper from "@/components/dynamic-form/dynamicFormHelper";
import HelpTrigger, { TYPES as HELP_TRIGGER_TYPES } from "@/components/help-trigger/HelpTrigger";
import { useTheme } from "@/components/ThemeProvider";
import "./CreateSourceDialog.less";
import classNames from "classnames";

const { Step } = Steps;
const { Search } = Input;

const StepEnum = {
  SELECT_TYPE: 0,
  CONFIGURE_IT: 1,
  DONE: 2,
};

function CreateSourceDialog({ dialog, types, sourceType, imageFolder, helpTriggerPrefix, onCreate }) {
  const { isDarkMode } = useTheme();
  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [savingSource, setSavingSource] = useState(false);
  const [currentStep, setCurrentStep] = useState(StepEnum.SELECT_TYPE);

  const formId = useRef(uniqueId("sourceForm"));

  const selectType = (selectedType) => {
    setSelectedType(selectedType);
    setCurrentStep(StepEnum.CONFIGURE_IT);
  };

  const resetType = () => {
    if (currentStep === StepEnum.CONFIGURE_IT) {
      setSearchText("");
      setSelectedType(null);
      setCurrentStep(StepEnum.SELECT_TYPE);
    }
  };

  const createSource = (values, successCallback, errorCallback) => {
    if (!savingSource) {
      setSavingSource(true);
      setCurrentStep(StepEnum.DONE);
      onCreate(selectedType, values)
        .then(data => {
          successCallback("Saved.");
          dialog.close({ success: true, data });
        })
        .catch(error => {
          setSavingSource(false);
          setCurrentStep(StepEnum.CONFIGURE_IT);
          errorCallback(get(error, "response.data.message", "Failed saving."));
        });
    }
  };

  const renderTypeSelector = () => {
    const filteredTypes = types.filter(
      type => isEmpty(searchText) || includes(type.name.toLowerCase(), searchText.toLowerCase())
    );
    return (
      <div className="m-t-10">
        <Search
          placeholder="Search..."
          aria-label="Search"
          onChange={e => setSearchText(e.target.value)}
          autoFocus
          data-test="SearchSource"
        />
        <div className="scrollbox p-5 m-t-10" style={{ minHeight: "30vh", maxHeight: "40vh" }}>
          {isEmpty(filteredTypes) ? (
            <EmptyState className="" />
          ) : (
            <List size="small" dataSource={filteredTypes} renderItem={item => renderItem(item)} />
          )}
        </div>
      </div>
    );
  };

  const renderForm = () => {
    const fields = helper.getFields(selectedType);
    const helpTriggerType = `${helpTriggerPrefix}${toUpper(selectedType.type)}`;
    return (
      <div>
        <div className="d-flex justify-content-center align-items-center">
          <img className="p-5" src={`${imageFolder}/${selectedType.type}.png`} alt={selectedType.name} width="48" />
          <h4 className="m-0">{selectedType.name}</h4>
        </div>
        <div className="text-right">
          {HELP_TRIGGER_TYPES[helpTriggerType] && (
            <HelpTrigger className="f-13" type={helpTriggerType}>
              Setup Instructions <i className="fa fa-question-circle" aria-hidden="true" />
              <span className="sr-only">(help)</span>
            </HelpTrigger>
          )}
        </div>
        <DynamicForm id={formId.current} fields={fields} onSubmit={createSource} feedbackIcons hideSubmitButton />
        {selectedType.type === "databricks" && (
          <small>
            By using the Databricks Data Source you agree to the Databricks JDBC/ODBC{" "}
            <Link href="https://databricks.com/spark/odbc-driver-download" target="_blank" rel="noopener noreferrer">
              Driver Download Terms and Conditions
            </Link>
            .
          </small>
        )}
      </div>
    );
  };

  const renderItem = (item) => {
    return (
      <List.Item className="p-l-10 p-r-10 clickable" onClick={() => selectType(item)}>
        <PreviewCard
          title={item.name}
          imageUrl={`${imageFolder}/${item.type}.png`}
          roundedImage={false}
          data-test="PreviewItem"
          data-test-type={item.type}>
          <i className="fa fa-angle-double-right" aria-hidden="true" />
        </PreviewCard>
      </List.Item>
    );
  };

  return (
    <Modal
      {...dialog.props}
      title={`Create a New ${sourceType}`}
      footer={
        currentStep === StepEnum.SELECT_TYPE
          ? [
              <Button key="cancel" onClick={() => dialog.dismiss()} data-test="CreateSourceCancelButton">
                Cancel
              </Button>,
              <Button key="submit" type="primary" disabled>
                Create
              </Button>,
            ]
          : [
              <Button key="previous" onClick={resetType}>
                Previous
              </Button>,
              <Button
                key="submit"
                htmlType="submit"
                form={formId.current}
                type="primary"
                loading={savingSource}
                data-test="CreateSourceSaveButton">
                Create
              </Button>,
            ]
      }
      className={classNames("create-source-dialog-wrapper", { "dark-mode": isDarkMode })}
    >
      <div className={classNames("create-source-dialog-content", { "dark-mode": isDarkMode })} data-test="CreateSourceDialog">
        <Steps className="hidden-xs m-b-10" size="small" current={currentStep} progressDot>
          {currentStep === StepEnum.CONFIGURE_IT ? (
            <Step title={<a>Type Selection</a>} className="clickable" onClick={resetType} />
          ) : (
            <Step title="Type Selection" />
          )}
          <Step title="Configuration" />
          <Step title="Done" />
        </Steps>
        {currentStep === StepEnum.SELECT_TYPE && renderTypeSelector()}
        {currentStep !== StepEnum.SELECT_TYPE && renderForm()}
      </div>
    </Modal>
  );
}

CreateSourceDialog.propTypes = {
  dialog: DialogPropType.isRequired,
  types: PropTypes.array.isRequired,
  sourceType: PropTypes.string.isRequired,
  imageFolder: PropTypes.string.isRequired,
  helpTriggerPrefix: PropTypes.string,
  onCreate: PropTypes.func.isRequired,
};

export default wrapDialog(CreateSourceDialog);
